import React, { useEffect, useState } from 'react';
import { Row } from 'antd';
import { DateTime } from 'luxon';

import { SettingOutlined, SignatureOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useToggle from '../../common/hooks/useToggle';
import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import WorkflowExternalSignatureConfigureDrawer from './WorkflowExternalSignatureConfigureDrawer';

const getText = (isConnected) => (
  <div>
    This step will generate a Docusign envelope for the form
    and send it to the configured signers.
    {!isConnected && (
      <>
        <br />
        <br />
        The Docusign integration must be configured.
      </>
    )}
  </div>
);

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires at least one <b>Select Recipient(s)</b> field to be set via <b> Configure</b>.
    </div>
  ),
};

export default function WorkflowExternalSignatureNode({
  setDataMap,
  setElements,
  isDisplay,
  divisionId,
  divisionIds = [],
  sections = [],
  isExternalForm,
  setDisableNext,
  isV3Form,
} = {}) {
  return function _({
    id,
    draggable,
    data,
    disabled,
  } = {}) {
    const title = `Collect External Signature${disabled ? ' - DISABLED' : ''}`;

    const {
      signers: initialSigners = [{
        id: DateTime.local().toMillis(),
        order: 1,
        emails: [],
      }],
    } = data ?? {};

    const [showErrorDetails, setShowErrorDetails] = useState(false);
    const [signers, setSigners] = useState(initialSigners || []);

    const {
      connectedToDocusign,
    } = useSelector((state) => state.settings.company) ?? {};

    const {
      isToggled: showDrawer,
      toggle: toggleDrawer,
    } = useToggle();

    useEffect(() => {
      if (!setDisableNext) return;

      const hasValidSigner = signers
        .some((signer) => Array.isArray(signer.emails) && signer.emails.length > 0);

      setDisableNext((prev) => ({ ...prev, [id]: !hasValidSigner }));
      setShowErrorDetails(!hasValidSigner);
    }, [signers, setDisableNext, id]);

    return (
      <WorkflowActionNode
        title={title}
        Icon={SignatureOutlined}
        type="externalSignature"
        id={id}
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={getText(connectedToDocusign)}
        style={disabled ? { opacity: 0.7 } : { width: 250 }}
        errorPopover={showErrorDetails ? ERROR_DETAILS : undefined}
      >
        {!draggable && (
          <div>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggleDrawer}
              />
            </Row>

            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
            <WorkflowExternalSignatureConfigureDrawer
              id={id}
              visible={showDrawer}
              onClose={toggleDrawer}
              onSubmit={toggleDrawer}
              divisionId={divisionId}
              divisionIds={divisionIds}
              sections={sections}
              data={data}
              setDataMap={setDataMap}
              setElements={setElements}
              isExternalForm={isExternalForm}
              signers={signers}
              setSigners={setSigners}
              isV3Form={isV3Form}
            />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
